import React from 'react'

import { flow } from 'lodash'

import { observer } from 'mobx-react'
import { makeStyles, Theme, createStyles, Container } from '@material-ui/core'
import Form from './Form'
import ParamTopicProvider from '../Providers/ParamTopicProvider'
import { useParams, withRouter, RouteComponentProps } from 'react-router'
import ParamConversationProvider from '../Providers/ParamConversationProvider'

/**
 * The story submission page.
 *
 * This used to be a two-column layout: the form on the left, and on the right
 * three Inclusivv-branded stock images hardcoded against
 * `civicdinners-manual-storage.s3.amazonaws.com` -- a third party's personal
 * S3 account this platform is actively migrating off. This is the page a guest
 * lands on after scanning the story QR code, so that art was both off-brand
 * for the theatre and a live read from an account that can disappear.
 *
 * There is no replacement imagery, so the column is removed rather than
 * re-pointed and the form takes the full width. Two of the three images were
 * already `Hidden xsDown`, so on a phone -- the device a QR scan lands on --
 * almost nothing changes. `maxWidth='sm'` keeps the form the same measure it
 * had inside the old `md={6}` column, so it does not stretch on a desktop.
 *
 * Adding OOH's own imagery back here is a separate piece of work.
 */
const StoryCollection = (props: RouteComponentProps) => {
  const classes = Styles()
  const { conversationId } = useParams<{ conversationId?: string }>()

  const page = (
    <div className={classes.root}>
      <Container maxWidth='sm' className={classes.main}>
        <Form />
      </Container>
    </div>
  )

  /**
   * `ParamConversationProvider` is mounted only on the route that actually
   * names a conversation.
   *
   * It reads the id straight out of the route with `parseInt`, and on
   * `/:slug/story` there is no id to read: the result is `NaN`, which it then
   * hands to `conversations.loadById` -- a `GET /conversations/NaN` on every
   * scan of a QR card. Skipping the provider is enough to avoid it.
   * `ParamConversationContext` has a default value, so `Form` still reads a
   * defined context with `conversation` undefined, which is exactly the
   * topic-level case.
   */
  return (
    <ParamTopicProvider>{conversationId ? <ParamConversationProvider>{page}</ParamConversationProvider> : page}</ParamTopicProvider>
  )
}
const Styles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      margin: '0 0',
      width: '100%',
      backgroundColor: '#F6F7F9'
    },
    main: {
      margin: '0 auto',
      paddingBottom: '3.4em'
    }
  })
)
export default flow(observer, withRouter)(StoryCollection)
